import { useState } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { AlertTriangle } from "lucide-react";
import type { Package } from "../../types/package";

interface DeletePackageDialogProps {
  isOpen: boolean;
  pkg: Package | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export function DeletePackageDialog({
  isOpen,
  pkg,
  onClose,
  onConfirm,
}: DeletePackageDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !pkg) return null;

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err: any) {
      setError(err?.message || "Failed to delete package");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Package">
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-lg border border-red-100 bg-red-50 p-3 text-red-700">
          <AlertTriangle size={20} />
          <p className="text-sm font-semibold">
            This will deactivate the package and make it unavailable for
            purchase.
          </p>
        </div>

        <p className="text-sm text-gray-600">
          Package <strong>"{pkg.title}"</strong> currently has{" "}
          <strong>{pkg.purchaseCount || 0}</strong> purchases. Deleting it will
          disable new enrollments but keep historical data for reporting.
        </p>

        {error && (
          <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete Package"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
